/**
 * AuthService.ts
 * Service d'authentification utilisant Supabase Auth
 */
import { supabase, isSupabaseConfigured } from './SupabaseService';
import type { User, Session, AuthError } from '@supabase/supabase-js';
import { logger } from '../utils/ProfessionalLogger';

export interface SignUpResult {
  user: User | null;
  session: Session | null;
  error: AuthError | null;
}

export interface SignInResult {
  user: User | null;
  session: Session | null;
  error: AuthError | null;
}

export class AuthService {
  /**
   * Inscription d'un nouvel utilisateur
   */
  async signUp(email: string, password: string, fullName?: string): Promise<SignUpResult> {
    if (!isSupabaseConfigured() || !supabase) {
      const error = { message: "Supabase n'est pas configuré", status: 500 } as AuthError;
      logger.warn("Tentative d'inscription sans configuration Supabase");
      return { user: null, session: null, error };
    }
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName || '',
          },
        },
      });

      if (error) {
        logger.error("Erreur lors de l'inscription:", error);
        return { user: null, session: null, error };
      }

      logger.info('✅ Utilisateur inscrit avec succès:', data.user?.email);
      return {
        user: data.user,
        session: data.session,
        error: null,
      };
    } catch (error) {
      logger.error("Exception lors de l'inscription:", error);
      return {
        user: null,
        session: null,
        error: error as AuthError,
      };
    }
  }

  /**
   * Connexion d'un utilisateur
   */
  async signIn(email: string, password: string): Promise<SignInResult> {
    if (!isSupabaseConfigured() || !supabase) {
      const error = { message: "Supabase n'est pas configuré", status: 500 } as AuthError;
      logger.warn('Tentative de connexion sans configuration Supabase');
      return { user: null, session: null, error };
    }
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        logger.error('Erreur lors de la connexion:', error);
        return { user: null, session: null, error };
      }

      logger.info('✅ Utilisateur connecté:', data.user?.email);
      return {
        user: data.user,
        session: data.session,
        error: null,
      };
    } catch (error) {
      logger.error('Exception lors de la connexion:', error);
      return {
        user: null,
        session: null,
        error: error as AuthError,
      };
    }
  }

  /**
   * Connexion avec Google (OAuth)
   */
  async signInWithGoogle(): Promise<{ error: AuthError | null }> {
    if (!isSupabaseConfigured() || !supabase) {
      const error = { message: "Supabase n'est pas configuré", status: 500 } as AuthError;
      logger.warn('Tentative de connexion Google sans configuration Supabase');
      return { error };
    }
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (error) {
        logger.error('Erreur lors de la connexion Google:', error);
        return { error };
      }

      return { error: null };
    } catch (error) {
      logger.error('Exception lors de la connexion Google:', error);
      return { error: error as AuthError };
    }
  }

  /**
   * Déconnexion de l'utilisateur
   */
  async signOut(): Promise<{ error: AuthError | null }> {
    if (!isSupabaseConfigured() || !supabase) {
      logger.warn('Tentative de déconnexion sans configuration Supabase');
      return { error: null }; // Pas d'erreur si Supabase n'est pas configuré
    }
    try {
      const { error } = await supabase.auth.signOut();

      if (error) {
        logger.error('Erreur lors de la déconnexion:', error);
        return { error };
      }

      logger.info('✅ Utilisateur déconnecté');
      return { error: null };
    } catch (error) {
      logger.error('Exception lors de la déconnexion:', error);
      return { error: error as AuthError };
    }
  }

  /**
   * Récupère l'utilisateur actuel
   */
  async getCurrentUser(): Promise<User | null> {
    if (!isSupabaseConfigured() || !supabase) {
      return null;
    }
    try {
      const {
        data: { user },
        error,
      } = await supabase.auth.getUser();

      if (error) {
        logger.error("Erreur lors de la récupération de l'utilisateur:", error);
        return null;
      }

      return user;
    } catch (error) {
      logger.error("Exception lors de la récupération de l'utilisateur:", error);
      return null;
    }
  }

  /**
   * Récupère la session actuelle
   */
  async getSession(): Promise<Session | null> {
    if (!isSupabaseConfigured() || !supabase) {
      return null;
    }
    try {
      const {
        data: { session },
        error,
      } = await supabase.auth.getSession();

      if (error) {
        logger.error('Erreur lors de la récupération de la session:', error);
        return null;
      }

      return session;
    } catch (error) {
      logger.error('Exception lors de la récupération de la session:', error);
      return null;
    }
  }

  /**
   * Vérifie si l'utilisateur est authentifié
   */
  async isAuthenticated(): Promise<boolean> {
    const user = await this.getCurrentUser();
    return user !== null;
  }

  /**
   * Réinitialise le mot de passe
   */
  async resetPassword(email: string): Promise<{ error: AuthError | null }> {
    if (!isSupabaseConfigured() || !supabase) {
      const error = { message: "Supabase n'est pas configuré", status: 500 } as AuthError;
      logger.warn('Tentative de réinitialisation de mot de passe sans configuration Supabase');
      return { error };
    }
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/reset-password`,
      });

      if (error) {
        logger.error('Erreur lors de la réinitialisation du mot de passe:', error);
        return { error };
      }

      logger.info('✅ Email de réinitialisation envoyé');
      return { error: null };
    } catch (error) {
      logger.error('Exception lors de la réinitialisation du mot de passe:', error);
      return { error: error as AuthError };
    }
  }
}

// Instance singleton
export const authService = new AuthService();
